import { useState, useEffect, useMemo, useLayoutEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import Header from "@/components/Header";
import Hero from "@/components/Hero";
import CategoryGrid from "@/components/CategoryGrid";
import HomepageSections from "@/components/HomepageSections";
import About from "@/components/About";
import WhyUs from "@/components/WhyUs";
import WhyChooseAccordion from "@/components/WhyChooseAccordion";
import Newsletter from "@/components/Newsletter";
import { Footer2 } from "@/components/ui/shadcnblocks-com-footer2";
import PageTransition from "@/components/PageTransition";
import { Separator } from "@/components/ui/separator";
import { useGlobalPreload } from "@/hooks/useGlobalPreload";
import { SEO } from "@/components/SEO";
import { useStaticTranslation } from "@/lib/staticTranslations";

const Index = () => {
  const { t: seoT } = useStaticTranslation("seo");
  const location = useLocation();
  const hasScrolledToHash = useRef(false);
  
  // Preload all global data on first site visit
  const { isLoaded, isLoading, allCollections, siteSettings, preload } = useGlobalPreload();

  useEffect(() => {
    preload();
  }, [preload]);

  // Compute featured categories from preloaded data
  const categoryData = useMemo(() => {
    if (!isLoaded || allCollections.length === 0) return [];

    const featuredSetting = siteSettings["featured_categories"] as { handles?: string[] } | undefined;
    const featuredHandles = featuredSetting?.handles || [];

    let displayCollections = allCollections;
    if (featuredHandles.length > 0) {
      displayCollections = featuredHandles
        .map((handle) => allCollections.find((c) => c.node.handle === handle))
        .filter((c): c is (typeof allCollections)[0] => c !== undefined);
    } else {
      displayCollections = allCollections.slice(0, 8);
    }

    return displayCollections.map((collection) => ({
      id: collection.node.handle,
      name: collection.node.title,
      image: collection.node.image?.url || "",
      link: `/collections/${collection.node.handle}`,
    }));
  }, [isLoaded, allCollections, siteSettings]);

  // Handle hash-based navigation (e.g., /#about) AFTER content has loaded
  useEffect(() => {
    const hash = location.hash;
    
    // Only scroll once when content is loaded and there's a hash
    if (hash && isLoaded && !isLoading && !hasScrolledToHash.current) {
      const elementId = hash.replace('#', '');
      
      // Small delay to ensure DOM has rendered
      const scrollTimer = setTimeout(() => {
        const element = document.getElementById(elementId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
          hasScrolledToHash.current = true;
        }
      }, 100);
      
      return () => clearTimeout(scrollTimer);
    }
    
    // Reset when navigating away and back
    if (!hash) {
      hasScrolledToHash.current = false;
    }
  }, [location.hash, isLoaded, isLoading]);

  return (
    <PageTransition>
      <SEO
        title={seoT("homeTitle", "אליס | תכשיטים ושעונים - באר שבע")}
        description={seoT("homeDescription", "חברת התכשיטים המובילה בבאר שבע בדרום מאז 1984. באתר התכשיטים אונליין - מבחר ענק של טבעות אירוסין, טבעות נישואין, צמידים, שרשראות, תליונים, עגילים.")}
        canonical="https://alice.jewelry/"
        showOrganization={true}
        showWebsiteSchema={true}
      />
      <div className="min-h-screen" dir="rtl">
        <Header />
        <main>
          <Hero />
          <Separator className="bg-border/50 h-[2px]" />
          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          ) : categoryData.length > 0 ? (
            <CategoryGrid 
              title="קטגוריות מובילות" 
              categories={categoryData} 
            />
          ) : null}
          <Separator className="bg-border/50 h-[2px]" />
          <HomepageSections />
          <Separator className="bg-border/50 h-[2px]" />
          <About />
          <Separator className="bg-border/50 h-[2px]" />
          <WhyUs />
          <Separator className="bg-border/50 h-[2px]" />
          <Newsletter />
          <Separator className="bg-border/50 h-[2px]" />
          <WhyChooseAccordion />
        </main>
        <Footer2 />
      </div>
    </PageTransition>
  );
};

export default Index;
